import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { memo, useCallback, useMemo } from 'react';
import { Grid } from '@mui/material';
import { UniversalDialog } from '../universal';
import { isMediumUI } from 'shared/app/lib/choose-ui-type';
import { MobileDialogAppBar } from './mobile-app-bar';
import { pop_back_item, push_back_item } from '../../utils/back-button';
import { useBox, usePartial } from 'shared/hooks/utils';
import { SaveApplyButtons } from 'z3k-settings/layout/save-apply-buttons';
import { DialogSaveType, } from '../cool-settings-dialog';
import { Z3kDialogFooter } from 'z3k-settings/layout/footer';
import { ErrorBoundary } from 'shared/components/error-boundary';
import { SettingMediumInterface } from './medium-ui';
import { SettingSmallInterface } from './small-ui';
import { setServiceModeManual } from 'console/redux/devices/items/actions';
import { useAction, useAppSelector } from 'shared/hooks/redux/hooks';
import { selectDevice } from 'entities/device/lib';
function BaseSettingsDialogImpl(props) {
    const { open, onRequestClose, onTrySave, title, save_disabled, selected_tab_key, onChangeTab, save_visible, need_service_view, service_mode, onSetServiceMode, password, tabs, onChangeState, volatile_data, dirty, additionalHeader, mobileDialogAppBar, } = props;
    const is_medium_ui = isMediumUI();
    const onSave = usePartial(onTrySave, DialogSaveType.Save);
    const onApply = usePartial(onTrySave, DialogSaveType.Apply);
    const device_id = useAppSelector((state) => state.devices.selected_id);
    const device = useAppSelector((state) => selectDevice(state, device_id));
    const manual = device?.service_mode_manual;
    const setManual = useAction(setServiceModeManual);
    const onSetServiceView = useCallback((is_service_view) => {
        if (device_id && !manual) {
            setManual(device_id);
        }
        return onSetServiceMode?.(is_service_view);
    }, [onSetServiceMode, device_id, manual, setManual]);
    const _selected_tab_key = is_medium_ui
        ? selected_tab_key ?? tabs[0].key
        : selected_tab_key;
    const selected_tab = tabs.find((tab) => _selected_tab_key === tab.key);
    const onSelectTab = useCallback((tab_key) => {
        if (_selected_tab_key == null && tab_key != null) {
            // forward
            push_back_item(onBrowserBack);
        }
        if (_selected_tab_key != null && tab_key == null) {
            // backward
            pop_back_item(onBrowserBack);
        }
        if (_selected_tab_key != null && tab_key != null) {
            // teleport
        }
        onChangeTab(tab_key);
    }, [_selected_tab_key, onChangeTab]);
    const onBack = usePartial(onSelectTab, null);
    const onSelectTabBox = useBox(onSelectTab);
    const onBrowserBack = useCallback(() => {
        onSelectTabBox.current(null);
    }, []);
    const pages = useMemo(() => tabs.map((tab) => ({
        key: tab.key,
        title: tab.title,
        warning: false,
        error: tab.validation != null,
        selected: _selected_tab_key === tab.key,
        data_tooltip: tab.data_tooltip,
    })), [tabs, _selected_tab_key]);
    return (_jsx(UniversalDialog, { title: title, open: open, onClose: onRequestClose, fullWidth: true, sx: {
            ...(is_medium_ui && {
                '& .MuiDialog-paper': {
                    m: 1,
                    height: '100%',
                    maxWidth: '800px',
                },
                '& .MuiDialog-paperScrollPaper': {
                    maxHeight: '80%',
                },
            }),
        }, content_sx: is_medium_ui
            ? {
                display: 'flex',
                flexFlow: 'row nowrap',
            }
            : undefined, contentWithZeroPadding: true, noIPhoneBottomPadding: true, fullScreenAppBar: is_medium_ui ? null : mobileDialogAppBar == null ? (_jsx(MobileDialogAppBar, { active_tab_title: selected_tab?.title ?? null, active_tab_id: _selected_tab_key, default_tab_title: title, onBack: onBack, onClose: onRequestClose })) : (mobileDialogAppBar), footer: is_medium_ui ? (_jsx(ErrorBoundary, { children: !save_visible ? null : need_service_view ? (_jsx(Z3kDialogFooter, { supportsServiceView: true, is_service_view: !!service_mode, password: password ?? '', default_password: "0000", loading: save_disabled, onSetServiceView: onSetServiceView, onSave: onSave, onApply: onApply })) : (_jsx(Grid, { container: true, justifyContent: "flex-end", children: _jsx(Grid, { item: true, children: _jsx(SaveApplyButtons, { loading: save_disabled, onSave: onSave, onApply: onApply }) }) })) })) : null, additionalHeader: additionalHeader, children: is_medium_ui ? (_jsx(SettingMediumInterface, { selected_tab: selected_tab, pages: pages, onSelectTab: onSelectTab, onChangeState: onChangeState, volatile_data: volatile_data })) : (_jsx(SettingSmallInterface, { tabs: tabs, selected_tab_key: _selected_tab_key, pages: pages, onSelectTab: onSelectTab, onChangeState: onChangeState, volatile_data: volatile_data, onSave: onSave, loading: save_disabled, dirty: dirty })) }));
}
export const BaseSettingsDialog = memo(BaseSettingsDialogImpl);
